import React, { useEffect, useState } from "react";
import { fetchBrands } from "../../../../../services/brandService";
import { ConnectBlogCard } from "./ConnectBlogCard";
import { disconnectHostedBlog, getHostedBlog, type HostedBlog } from "../services/blogPublishingService";

/**
 * Connections › "Your website's blog" (SGL-66). WordPress connects through its
 * own card above; every other site builder (Squarespace, Wix, GoDaddy…) gets a
 * blog hosted by Sagelyn on the Brand's own domain, set up here.
 */
export const WebsiteBlogSection: React.FC<{ customerId: string; idToken: string; inDialog?: boolean }> = ({
  customerId,
  idToken,
  inDialog = false,
}) => {
  const [hosted, setHosted] = useState<HostedBlog | null>(null);
  const [brandName, setBrandName] = useState("");
  const [website, setWebsite] = useState("");
  const [platform, setPlatform] = useState<"squarespace" | "wix" | "other">("squarespace");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setHosted(null);
    setError(null);
    getHostedBlog(customerId, idToken)
      .then((h) => active && setHosted(h))
      .catch((e: unknown) => active && setError(e instanceof Error ? e.message : "Failed to load"));
    fetchBrands(customerId, idToken)
      .then(({ brands }) => {
        if (!active) return;
        setBrandName((brands?.[0]?.brand?.name ?? "").trim());
        setWebsite((brands?.[0]?.brand?.website_url ?? "").trim());
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [customerId, idToken, reloadKey]);

  const disconnect = async () => {
    setBusy(true);
    setError(null);
    try {
      await disconnectHostedBlog(customerId, idToken);
      setHosted({ connected: false });
      setConfirming(false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to disconnect");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      aria-labelledby="website-blog-h"
      className={inDialog ? "rounded-xl bg-white p-6 shadow-xl" : "mt-10 rounded-xl bg-zinc-50 p-6 shadow-md"}
    >
      <h2 id="website-blog-h" className="text-xl font-semibold text-gray-800">
        Your website's blog
      </h2>
      <p className="mt-1 text-sm text-gray-500">
        Squarespace, Wix, GoDaddy and most site builders don't let other apps post to their blog, so Sagelyn hosts
        your blog on your own domain. Your main site stays where it is. On WordPress? Use the WordPress card instead.
      </p>
      {error && <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      {hosted === null && error ? (
        <button
          type="button"
          onClick={() => setReloadKey((k) => k + 1)}
          className="mt-4 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
        >
          Try again
        </button>
      ) : hosted === null ? (
        <p className="mt-4 text-sm text-gray-500">Loading…</p>
      ) : (
        <div className="mt-4 max-w-2xl space-y-3">
          {!hosted.connected && (
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Your site builder">
              {(
                [
                  ["squarespace", "Squarespace"],
                  ["wix", "Wix"],
                  ["other", "GoDaddy or other"],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={platform === key}
                  onClick={() => setPlatform(key)}
                  className={`rounded-lg border px-4 py-2 text-sm font-semibold ${
                    platform === key ? "border-brand-primary bg-white text-brand-primary" : "border-gray-200 bg-white text-gray-600"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          <ConnectBlogCard
            key={hosted.connected ? hosted.destinationId : "new"}
            customerId={customerId}
            idToken={idToken}
            businessName={brandName}
            websiteUrl={website}
            platform={platform}
            initial={hosted}
            onChange={setHosted}
          />
          {/* While waiting for DNS the card's own "Use a different address" covers this. */}
          {hosted.connected && hosted.status === "CONNECTED" &&
            (confirming ? (
              <div className="flex gap-2">
                <button type="button" onClick={disconnect} disabled={busy} className="rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white disabled:opacity-60">
                  {busy ? "Disconnecting…" : "Yes, disconnect"}
                </button>
                <button type="button" onClick={() => setConfirming(false)} className="rounded-lg px-3 py-2 text-xs font-semibold text-gray-500">
                  Cancel
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => setConfirming(true)} className="text-xs font-semibold text-gray-500 hover:text-red-600">
                Disconnect this blog
              </button>
            ))}
        </div>
      )}
    </section>
  );
};

export default WebsiteBlogSection;
